import React, { memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Input } from 'antd';

export type ILmsBlockModalMode = 'block' | 'unblock';

interface IProps {
  mode: ILmsBlockModalMode;
  loading?: boolean;
  onSubmit: (reason?: string) => void;
  onCancel: () => void;
}

/**
 * Shared LMS-access block/unblock dialog (spec 0003). Purely presentational:
 * it collects the optional block reason (or shows the unblock confirmation) and
 * hands the value back via `onSubmit` — it never calls a mutation itself, so the
 * student-card and group-debtors call sites drive their own hooks.
 */
export const LmsBlockModal = memo<IProps>(
  ({ mode, loading, onSubmit, onCancel }) => {
    const { t } = useTranslation();
    const [reason, setReason] = useState('');

    const isBlock = mode === 'block';

    const onOk = useCallback(() => {
      onSubmit(isBlock ? reason.trim() || undefined : undefined);
    }, [isBlock, reason, onSubmit]);

    return (
      <Modal
        visible
        title={
          isBlock
            ? t('pages.studentDetail.lmsBlock.block')
            : t('pages.studentDetail.lmsBlock.unblock')
        }
        onOk={onOk}
        onCancel={onCancel}
        confirmLoading={loading}
        okText={
          isBlock
            ? t('pages.studentDetail.lmsBlock.blockConfirm')
            : t('pages.studentDetail.lmsBlock.unblockConfirm')
        }
        cancelText={t('button.cancel')}
        okButtonProps={{ danger: isBlock }}
        destroyOnClose
      >
        {isBlock ? (
          <>
            <div style={{ marginBottom: 8 }}>
              {t('pages.studentDetail.lmsBlock.reasonPrompt')}
            </div>
            <Input.TextArea
              autoFocus
              rows={3}
              maxLength={500}
              value={reason}
              placeholder={t('pages.studentDetail.lmsBlock.reasonPlaceholder')}
              onChange={(e) => setReason(e.target.value)}
            />
          </>
        ) : (
          <div>{t('pages.studentDetail.lmsBlock.confirmUnblock')}</div>
        )}
      </Modal>
    );
  },
);
